import i18next from 'i18next';
i18next.loadNamespaces(['activity-api', 'units']);

//TODO remove this and the related code after api side updated the get unit logic in WorkoutBuilder
export const poolLengthUnit = {
    meter: {
        unitId: 1,
        unitKey: 'meter',
        factor: 100
    },
    yard: {
        unitId: 230,
        unitKey: 'yard',
        factor: 91.44
    }
};

/**
 * TODO: remove the Backbone references when Backbone code is gone
/*

/** Constants */
export const poolSizeUnit = {
    meter: 'meter',
    yard: 'yard'
};

export const poolSizeUnitShort = {
    meter: 'm',
    yard: 'y'
};

export const poolSizes = {
    twentyFiveMeters: '25 m',
    twentyFiveYards: '25 y',
    fiftyMeters: '50 m',
    thirtyThreeOneThirdMeters: '33.3333 m',
    thirtyThreeOneThirdYards: '33.3333 y',
    custom: 'custom',
    unspecified: 'unspecified'
};

// CA-169978: remove oldPoolSizes and references once the poolSwim2 feature is released and works well
export const oldPoolSizes = {
    twentyFiveMeters: '25 m',
    thirtyThreeOneThirdMeters: '33.3333 m',
    fiftyMeters: '50 m',
    twentyFiveYards: '25 y',
    thirtyThreeOneThirdYards: '33.3333 y',
    custom: 'custom'
};

export const DEFAULT_POOL_SIZE = poolSizes.twentyFiveMeters;

export const defaultUserPoolSizePreferenceKey = 'default_user_pool_size';

const fractionalPoolLength = 33.3333;

/** Util Functions */
/**
 * Description: Returns the fraction value of a decimal, only returns fraction for 1/3 and 2/3, other values are rounded to nearest integer
 * Some Examples:
 *   - '25'      -> '25'
 *   - '33.3333' -> '33 1/3'
 * Backbone reference: 'getFractionalValue' & 'fraction' in poolLengthUtil.js
 * TODO: refactor this function to a more readable version
 *
 * @param {string | number} value
 * @returns {string}
 */
export const getFractionalValue = (value) => {
    // truncate value to max of 4 digits right of the decimal point, this prevents errors with rounding
    let stringValue = String(parseFloat((+value).toFixed(4)))
            .trim()
            .split('.'),
        whole = '',
        decimal = 0,
        eps = 1.0e-15,
        h,
        h1,
        h2,
        k,
        k1,
        k2,
        a,
        x;

    // if value is a float x+.xxxx+ only caluculate 1/3 or 2/3, any other values are just rounded to nearest integer
    if (stringValue.length === 2 && ['3333', '6666'].includes(stringValue[1])) {
        whole = stringValue[0];
        decimal = parseFloat('.' + stringValue[1]);
        x = decimal;
        a = Math.floor(x);
        h1 = 1;
        k1 = 0;
        h = a;
        k = 1;

        while (x - a > eps * k * k) {
            x = 1 / (x - a);
            a = Math.floor(x);
            h2 = h1;
            h1 = h;
            k2 = k1;
            k1 = k;
            h = h2 + a * h1;
            k = k2 + a * k1;
        }

        if (h1 === 1 && k1 === 1) {
            //round up - ex: 99.99 will be 100
            return parseInt(whole, 10) + 1;
        }
        if (h2 === 0) {
            return (whole === 0 ? '' : whole + ' ') + h1 + '/' + k1;
        }
        return (whole === 0 ? '' : whole + ' ') + h2 + '/' + k2;
    }

    // if not 1/3 or 2/3 then round to the nearest integer
    return Math.round(+value);
};

/**
 * Backbone Reference: correctDecimalPrecision() in backbone/pages/workouts/utils/PoolLengthUtil.js
 * @param {string} value
 * @returns {string}
 */
export const correctDecimalPrecision = (value) => {
    //not what I wanted but calculating floating point values
    //has proven to be inconsistent (http://floating-point-gui.de/)
    //even when using libraries that claim to solve this problem, such
    //as sinful.js
    if (value) {
        const regex = /\.\d(0{4}|1{4}|2{4}|3{4}|4{4}|5{4}|6{4}|7{4}|8{4}|9{4})/;
        if (regex.exec(value) === null) {
            let whole,
                d,
                valueString = String(value),
                sp = valueString.split('.');

            if (sp.length === 2) {
                whole = sp[0];
                d = sp[1].slice(0, 1);
                value = parseFloat(whole + '.' + d + d + d + d);
            }
        }
    }
    return value;
};

/**
 * Returns the converted pool size string
 *
 * @param {string} length
 * @param {string} unit
 * @returns {string} converted pool size string, e.g. 25 m, 33.3333 y
 */
export const convertLengthUnitToPoolSize = (length, unit) => {
    // we need to convert the length string to number here because the length might start with '0', e.g. '025'
    // in which case, we should return '25 m(y)'
    return `${+length} ${unit === poolSizeUnit.yard ? poolSizeUnitShort.yard : poolSizeUnitShort.meter}`;
};

/**
 * Revert of convertLengthUnitToPoolSize
 * @param {string} poolSize
 * @returns {{length: string, unitKey: string}}
 */
export const convertPoolSizeToLengthUnit = (poolSize) => {
    // poolLength === null means poolLength is Unspecified, unitKey must also be null to hide units in UI
    const [length, unitKey] = poolSize === null ? [null, null] : poolSize.split(' ');
    return {
        length,
        unitKey: unitKey === null ? null : unitKey === poolSizeUnitShort.meter ? poolSizeUnit.meter : poolSizeUnit.yard
    };
};

const poolLengthsMultipliers = [1, 2, 3, 4, 5, 6, 8, 10, 12, 16, 20];

/**
 * Backbone Reference: getAvailablePoolLengths() in backbone/pages/workouts/utils/PoolLengthUtil.js
 * @param {string | number} poolLength
 * @param {string} poolLengthUnitKey
 * @returns {{
 *  displayValue: string;
 *  value: string;
 *  unit: string;
 *  postfixLabel: string | null;
 * }[]}
 */
export const getAvailablePoolLengths = (poolLength, poolLengthUnitKey) => {
    const getList = (poolSize, unit) => {
        return poolLengthsMultipliers.map((multiplier) => {
            const isDecimal = poolSize % 1 !== 0;
            const multipliedValue = correctDecimalPrecision(poolSize * multiplier);
            let displayText = String(multipliedValue);
            let postfixLabel = null;
            if (isDecimal) {
                postfixLabel = `(${multiplier} ${
                    multiplier > 1 ? i18next.t('activity-api:swim.lengths') : i18next.t('activity-api:label.length')
                })`;
                displayText = getFractionalValue(multipliedValue);
            }
            return {
                displayValue: displayText,
                value: multipliedValue,
                unit,
                postfixLabel
            };
        });
    };

    const unitText = unitAbbrevTranslation(poolLengthUnitKey);

    return getList(Number(poolLength), unitText);
};

/**
 * abbreviation translation for pool length unit
 * meter -> m
 * yard -> yd
 * @param {string} unitKey
 * @returns {string}
 */
export const unitAbbrevTranslation = (unitKey) => {
    return unitKey === 'meter'
        ? i18next.t('units:pool_length_unit_metric')
        : i18next.t('units:pool_length_unit_statute');
};

/**
 * Return number of lengths
 * 1. round the distance to the nearest multiple of the new pool size
 * 2. if the distance is less than 1 length, rount up to 1 pool length
 * 3. if the distance is exactly halfway in between two pool sizes, round down to the nearest multiple of the pool size
 * @param {number} distance
 * @param {number} poolLength
 * @returns {number}
 */
export const roundToNearestPoolLength = (distance, poolLength) => {
    if (distance < poolLength) {
        return 1;
    }
    if (distance % poolLength === poolLength / 2) {
        return Math.floor(distance / poolLength);
    }
    return Math.round(distance / poolLength);
};

// CA-169978: remove showPoolSwim2 logic once the feature is released and works well
export const getPoolSizeItems = (showPoolSwim2) => {
    return showPoolSwim2 ? poolSizes : oldPoolSizes;
};
